import styles from './ReportFilterBar.module.css';

/**
 * ReportFilterBar — shared toolbar for report pages that use the single-row
 * title + date range + course filter + Apply pattern.
 *
 * Props:
 *   title          string      — page title (e.g. "Occupancy rate")
 *   dateFrom       string      — controlled ISO date value
 *   dateTo         string      — controlled ISO date value
 *   onDateFromChange fn        — onChange handler for dateFrom
 *   onDateToChange   fn        — onChange handler for dateTo
 *   courseId       string      — controlled course ID ('' = all)
 *   courses        [{id, name}] — list from API
 *   onCourseChange fn          — onChange handler for courseId select
 *   onApply        fn          — called when Apply button is clicked
 *   onClear        fn          — optional; if provided renders a secondary
 *                               Clear button right after Apply
 *   extraFilters   ReactNode   — optional slot rendered between course select
 *                               and Apply button (e.g. Hole select)
 *   children       ReactNode   — optional slot rendered after Apply button
 *                               (e.g. Export button)
 *   dateFromLabel  string      — label for first date input (default: "From")
 *   dateToLabel    string      — label for second date input (default: "To")
 */
export default function ReportFilterBar({
  title,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  courseId,
  courses = [],
  onCourseChange,
  onApply,
  onClear,
  extraFilters,
  children,
  dateFromLabel = 'From',
  dateToLabel = 'To',
}) {
  return (
    <div className={styles.toolbar}>
      {title && <span className={styles.toolbarTitle}>{title}</span>}
      {title && <span className={styles.sep} />}

      <label className={styles.label}>
        {dateFromLabel} <span className={styles.req}>*</span>
      </label>
      <input
        type="date"
        className={styles.dateInput}
        value={dateFrom}
        onChange={e => onDateFromChange(e.target.value)}
      />

      <label className={styles.label}>
        {dateToLabel} <span className={styles.req}>*</span>
      </label>
      <input
        type="date"
        className={styles.dateInput}
        value={dateTo}
        onChange={e => onDateToChange(e.target.value)}
      />

      <span className={styles.sep} />

      <label className={styles.label}>Course:</label>
      <select
        className={styles.select}
        value={courseId}
        onChange={e => onCourseChange(e.target.value)}
      >
        <option value="">All courses</option>
        {courses.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      {extraFilters}

      <button className={styles.btnPrimary} onClick={onApply}>Apply</button>
      {onClear && <button className={styles.btnSecondary} onClick={onClear}>Clear</button>}

      {children}
    </div>
  );
}
